import React, { PropsWithChildren, ReactNode } from 'react';
import { TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import InfoIcon from '@material-ui/icons/Info';
import WarningIcon from '@material-ui/icons/Warning';
import WhatshotIcon from '@material-ui/icons/Whatshot';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getHeadingId } from './utils';
import {
  BulletItem,
  BulletList,
  MarkdownAdmonition,
  MarkdownAdmonitionBody,
  MarkdownAdmonitionHeader,
  MarkdownAdmonitionIcon,
  MarkdownAdmonitionTitle,
  MarkdownCodeBlockContainer,
  MarkdownCodeBody,
  MarkdownCodeCopyIcon,
  MarkdownCodeHeader,
  MarkdownCodeLine,
  MarkdownCodeLineNumber,
  MarkdownCodeLineText,
  MarkdownInlineCode,
  MarkdownDocParagraph,
  MarkdownDocTable,
  MarkdownDocTitle,
  ParagraphText,
  SectionTitle,
} from './styles';

const headingLevel2Style = { marginTop: 24, marginBottom: 16 };
const headingLevel3PlusStyle = { marginTop: 24, marginBottom: 8 };
const articleOrderedListStyle = { marginBottom: 16 };
const tabOrderedListStyle = { margin: '0 0 24px 0', paddingLeft: 20 };
const listItemStyle = { fontSize: 16, lineHeight: 1.5 };
const calloutPattern = /^\[!([a-zA-Z]+)(?:\|(.+))?\]$/;

type MarkdownDocVariant = 'article' | 'tab';

interface MarkdownDocProps {
  title?: string;
  markdown: string;
  variant?: MarkdownDocVariant;
}

interface BlockquoteProps {
  children?: ReactNode;
}

interface MarkdownCodeProps {
  children?: ReactNode;
  inline?: boolean;
  className?: string;
}

const ADMONITION_VARIANTS = ['warning', 'tip', 'danger', 'info'] as const;
type AdmonitionVariant = (typeof ADMONITION_VARIANTS)[number];

const calloutDefaultTitle: Record<AdmonitionVariant, string> = {
  warning: 'Warning',
  tip: 'Tip',
  danger: 'Danger',
  info: 'Info',
};

function isAdmonitionVariant(value: string): value is AdmonitionVariant {
  return ADMONITION_VARIANTS.some(variant => variant === value);
}

const admonitionIconByVariant: Record<AdmonitionVariant, ReactNode> = {
  warning: <WarningIcon fontSize="small" />,
  tip: <WhatshotIcon fontSize="small" />,
  danger: <FlashOnIcon fontSize="small" />,
  info: <InfoIcon fontSize="small" />,
};

function getAdmonitionIcon(variant: AdmonitionVariant) {
  return admonitionIconByVariant[variant];
}

function getNodeText(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(getNodeText).join('');
  }

  if (React.isValidElement<{ children?: ReactNode }>(node)) {
    return getNodeText(node.props.children);
  }

  return '';
}

function isWhitespaceTextNode(node: ReactNode): boolean {
  return (
    (typeof node === 'string' || typeof node === 'number') &&
    String(node).trim().length === 0
  );
}

function renderCodeBlock(rawText: string) {
  const source = rawText.replace(/\n$/, '');
  const lines = source.length > 0 ? source.split('\n') : [''];

  return (
    <MarkdownCodeBlockContainer>
      <MarkdownCodeHeader>
        <MarkdownCodeCopyIcon>
          <FileCopyOutlinedIcon fontSize="small" />
        </MarkdownCodeCopyIcon>
      </MarkdownCodeHeader>
      <MarkdownCodeBody>
        {lines.map((line, index) => (
          <MarkdownCodeLine key={`${index + 1}-${line}`}>
            <MarkdownCodeLineNumber>{index + 1}</MarkdownCodeLineNumber>
            <MarkdownCodeLineText>{line}</MarkdownCodeLineText>
          </MarkdownCodeLine>
        ))}
      </MarkdownCodeBody>
    </MarkdownCodeBlockContainer>
  );
}

function renderAdmonition(children: ReactNode) {
  const childNodes = React.Children.toArray(children).filter(
    node => !isWhitespaceTextNode(node),
  );
  const firstNodeIndex = childNodes.findIndex(node =>
    React.isValidElement<{ children?: ReactNode }>(node),
  );

  if (firstNodeIndex === -1) {
    return null;
  }

  const firstNode = childNodes[firstNodeIndex];

  if (!React.isValidElement<{ children?: ReactNode }>(firstNode)) {
    return null;
  }

  const markerText = getNodeText(firstNode.props.children).trim();
  const markerMatch = markerText.match(calloutPattern);

  if (!markerMatch) {
    return null;
  }

  const markerType = markerMatch[1].toLowerCase();
  const variant: AdmonitionVariant = isAdmonitionVariant(markerType)
    ? markerType
    : 'info';
  const title = markerMatch[2]?.trim() || calloutDefaultTitle[variant];
  const bodyNodes = childNodes.filter((_, index) => index !== firstNodeIndex);

  return (
    <MarkdownAdmonition data-variant={variant}>
      <MarkdownAdmonitionHeader data-variant={variant}>
        <MarkdownAdmonitionIcon data-variant={variant}>
          {getAdmonitionIcon(variant)}
        </MarkdownAdmonitionIcon>
        <MarkdownAdmonitionTitle>{title}</MarkdownAdmonitionTitle>
      </MarkdownAdmonitionHeader>
      <MarkdownAdmonitionBody>{bodyNodes}</MarkdownAdmonitionBody>
    </MarkdownAdmonition>
  );
}

function createMarkdownComponents(variant: MarkdownDocVariant) {
  function Heading({
    level,
    children,
  }: {
    level: 1 | 2 | 3 | 4 | 5 | 6;
    children: ReactNode;
  }) {
    const headingId = getHeadingId(getNodeText(children));

    if (level === 1) {
      if (variant === 'tab') {
        return (
          <SectionTitle id={headingId} variant="h4">
            {children}
          </SectionTitle>
        );
      }
      return <MarkdownDocTitle id={headingId}>{children}</MarkdownDocTitle>;
    }
    if (level === 2) {
      return (
        <SectionTitle id={headingId} variant="h5" style={headingLevel2Style}>
          {children}
        </SectionTitle>
      );
    }
    return (
      <SectionTitle id={headingId} variant="h6" style={headingLevel3PlusStyle}>
        {children}
      </SectionTitle>
    );
  }

  return {
    p: ({ children }: PropsWithChildren<object>) =>
      variant === 'tab' ? (
        <ParagraphText>{children}</ParagraphText>
      ) : (
        <MarkdownDocParagraph>{children}</MarkdownDocParagraph>
      ),
    h1: ({ children }: PropsWithChildren<object>) => (
      <Heading level={1}>{children}</Heading>
    ),
    h2: ({ children }: PropsWithChildren<object>) => (
      <Heading level={2}>{children}</Heading>
    ),
    h3: ({ children }: PropsWithChildren<object>) => (
      <Heading level={3}>{children}</Heading>
    ),
    h4: ({ children }: PropsWithChildren<object>) => (
      <Heading level={4}>{children}</Heading>
    ),
    h5: ({ children }: PropsWithChildren<object>) => (
      <Heading level={5}>{children}</Heading>
    ),
    h6: ({ children }: PropsWithChildren<object>) => (
      <Heading level={6}>{children}</Heading>
    ),
    ul: ({ children }: PropsWithChildren<object>) => (
      <BulletList>{children}</BulletList>
    ),
    ol: ({ children }: PropsWithChildren<object>) => (
      <ol
        style={
          variant === 'tab' ? tabOrderedListStyle : articleOrderedListStyle
        }
      >
        {children}
      </ol>
    ),
    li: ({ children }: PropsWithChildren<object>) =>
      variant === 'tab' ? (
        <BulletItem>{children}</BulletItem>
      ) : (
        <BulletItem style={listItemStyle}>{children}</BulletItem>
      ),
    pre: ({ children }: PropsWithChildren<object>) => <>{children}</>,
    code: ({ children, inline, className }: MarkdownCodeProps) => {
      const codeText = getNodeText(children);
      const hasLanguageClass = Boolean(
        className && className.startsWith('language-'),
      );
      const isInlineCode =
        inline ?? (!hasLanguageClass && !codeText.includes('\n'));

      if (isInlineCode) {
        return <MarkdownInlineCode>{codeText}</MarkdownInlineCode>;
      }

      return renderCodeBlock(codeText);
    },
    blockquote: ({ children }: BlockquoteProps) => {
      const callout = renderAdmonition(children);

      if (callout) {
        return callout;
      }

      return <blockquote>{children}</blockquote>;
    },
    table: ({ children }: PropsWithChildren<object>) => (
      <MarkdownDocTable>{children}</MarkdownDocTable>
    ),
    thead: ({ children }: PropsWithChildren<object>) => (
      <TableHead>{children}</TableHead>
    ),
    tbody: ({ children }: PropsWithChildren<object>) => (
      <TableBody>{children}</TableBody>
    ),
    tr: ({ children }: PropsWithChildren<object>) => (
      <TableRow>{children}</TableRow>
    ),
    th: ({ children }: PropsWithChildren<object>) => (
      <TableCell variant="head">{children}</TableCell>
    ),
    td: ({ children }: PropsWithChildren<object>) => (
      <TableCell>{children}</TableCell>
    ),
  };
}

export function MarkdownDoc({
  title,
  markdown,
  variant = 'article',
}: MarkdownDocProps) {
  const markdownComponents = createMarkdownComponents(variant);

  return (
    <div>
      {title && <MarkdownDocTitle>{title}</MarkdownDocTitle>}
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
